'use client';

/**
 * useLogin - Hook for authentication (v2)
 * Handles email/code authentication flow
 */

import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { setClientToken } from '../../../react/utils/tokenStorage';
import { SessionResource } from '../../core/resources/session';
import { getGlobalApiClient } from './useApiQuery';
import { usePluginConfig } from './usePluginConfig';

export interface UseLoginOptions {
  /**
   * Callback when login is successful
   */
  onSuccess?: (token: string) => void;

  /**
   * Callback when an error occurs
   */
  onError?: (error: Error) => void;
}

export interface UseLoginResult {
  /**
   * Request verification code for email
   */
  requestCode: (email: string) => Promise<{
    success: boolean;
    error?: string;
  }>;

  /**
   * Verify code and complete login
   */
  verifyCode: (email: string, code: string) => Promise<{
    success: boolean;
    token?: string;
    sessionId?: string;
    error?: string;
  }>;

  /**
   * Combined loading state (true if either request or verify is loading)
   */
  isLoading: boolean;

  /**
   * Loading state for request code
   */
  isRequestingCode: boolean;

  /**
   * Loading state for verify code
   */
  isVerifyingCode: boolean;

  /**
   * Combined error state
   */
  error: Error | null;

  /**
   * Error from request code
   */
  requestCodeError: Error | null;

  /**
   * Error from verify code
   */
  verifyCodeError: Error | null;
}

export function useLogin(options: UseLoginOptions = {}): UseLoginResult {
  const { onSuccess, onError } = options;
  const { storeId } = usePluginConfig();

  // Create session resource client
  const sessionResource = useMemo(() => {
    try {
      return new SessionResource(getGlobalApiClient());
    } catch (error) {
      throw new Error(
        'Failed to initialize session resource: ' +
        (error instanceof Error ? error.message : 'Unknown error')
      );
    }
  }, []);

  // Request code mutation
  const requestCodeMutation = useMutation({
    mutationFn: async (email: string) => {
      if (!storeId) {
        throw new Error('Store ID not found. Make sure the TagadaProvider is properly configured.');
      }

      return await sessionResource.requestCode(email, storeId);
    },
    onError: (error: Error) => {
      console.error('[SDK] Failed to request code:', error);
      onError?.(error);
    },
  });

  // Verify code mutation
  const verifyCodeMutation = useMutation({
    mutationFn: async ({ email, code }: { email: string; code: string }) => {
      if (!storeId) {
        throw new Error('Store ID not found. Make sure the TagadaProvider is properly configured.');
      }

      const result = await sessionResource.verifyCode(email, code, storeId);

      if (result.success && result.token) {
        // Store the token
        setClientToken(result.token);
        // Call success callback
        onSuccess?.(result.token);
      }

      return result;
    },
    onError: (error: Error) => {
      console.error('[SDK] Failed to verify code:', error);
      onError?.(error);
    },
  });

  // Action functions
  const requestCode = useCallback(
    async (email: string) => {
      return await requestCodeMutation.mutateAsync(email);
    },
    [requestCodeMutation]
  );

  const verifyCode = useCallback(
    async (email: string, code: string) => {
      return await verifyCodeMutation.mutateAsync({ email, code });
    },
    [verifyCodeMutation]
  );

  return {
    requestCode,
    verifyCode,
    isLoading: requestCodeMutation.isPending || verifyCodeMutation.isPending,
    isRequestingCode: requestCodeMutation.isPending,
    isVerifyingCode: verifyCodeMutation.isPending,
    error: requestCodeMutation.error || verifyCodeMutation.error,
    requestCodeError: requestCodeMutation.error,
    verifyCodeError: verifyCodeMutation.error,
  };
}

